/**
 * Organization & Identity (+ Installation) presentation adapters.
 * Maps existing checklist / org state — no business rules.
 */

import {OperationalStatusCardView, OperationalWarningView,} from '../operational-readiness.models';
import {OnboardingChecklistItem} from '../../organization-onboarding/organization-onboarding.types';

export const IDENTITY_CHECKLIST_KEYS = [
    'organization_created',
    'admin_staff_created',
    'admin_mobile_verified',
] as const;

/** Installation retains only non-infrastructure checklist steps. */
export const INSTALLATION_CHECKLIST_KEYS = [
    'first_vehicle_detection',
] as const;

const IDENTITY_TITLES: Record<string, string> = {
    organization_created: 'Organization Created',
    organization_active: 'Organization Active',
    admin_staff_created: 'Admin User Created',
    admin_mobile_verified: 'Admin Mobile Verified',
};

const INSTALLATION_TITLES: Record<string, string> = {
    first_vehicle_detection: 'First Vehicle Detection',
};

const IDENTITY_PENDING_WARNINGS: Record<string, OperationalWarningView> = {
    organization_created: {
        problem: 'Organization record is not available.',
        impact: 'Operational readiness cannot be evaluated for this tenant.',
        recommendedAction: 'Create the organization from Organization Admin.',
        scope: 'organization',
    },
    organization_active: {
        problem: 'Organization is inactive.',
        impact: 'Staff cannot sign in or use the platform for this organization.',
        recommendedAction: 'Enable the organization from Organization Admin.',
        scope: 'organization',
    },
    admin_staff_created: {
        problem: 'No active Admin user exists for this organization.',
        impact: 'Organization administrators cannot manage staff, cameras, or settings.',
        recommendedAction: 'Create an Admin staff account for this organization.',
        scope: 'organization',
    },
    admin_mobile_verified: {
        problem: 'No Admin user has a verified mobile number.',
        impact: 'Admins may be ineligible for mobile notifications until verification is complete.',
        recommendedAction: 'Have an Admin complete mobile verification (OTP).',
        scope: 'organization',
        condition: 'Required for installation checklist completion',
    },
};

const INSTALLATION_PENDING_WARNINGS: Record<string, OperationalWarningView> = {
    first_vehicle_detection: {
        problem: 'No vehicle detection has been received yet.',
        impact: 'Dashboard and logs will not show live vehicle activity until the first detection arrives.',
        recommendedAction: 'Confirm cameras, API keys, and provider delivery, then wait for the first event.',
        scope: 'organization',
    },
};

function checklistItemByKey(
    items: OnboardingChecklistItem[],
    key: string,
): OnboardingChecklistItem | undefined {
    return items.find((item) => item.key === key);
}

function readyCard(
    key: string,
    title: string,
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'ready',
        statusLabel: 'Ready',
        detail,
    };
}

function pendingCard(
    key: string,
    title: string,
    warning: OperationalWarningView | undefined,
    status: 'missing' | 'blocked' | 'warning' = 'missing',
    statusLabel = 'Pending',
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status,
        statusLabel,
        detail,
        warnings: warning ? [warning] : undefined,
    };
}

function unknownCard(key: string, title: string, detail: string): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'unknown',
        statusLabel: 'Unknown',
        detail,
    };
}

export function mapOrganizationIdentityCards(
    items: OnboardingChecklistItem[],
    organizationActive: boolean | null | undefined,
): OperationalStatusCardView[] {
    const cards: OperationalStatusCardView[] = [];

    for (const key of IDENTITY_CHECKLIST_KEYS) {
        const item = checklistItemByKey(items, key);
        const title = IDENTITY_TITLES[key];
        const completed = item?.completed === true;
        if (completed) {
            cards.push(readyCard(key, title));
        } else {
            cards.push(pendingCard(key, title, IDENTITY_PENDING_WARNINGS[key]));
        }
    }

    const activeTitle = IDENTITY_TITLES['organization_active'];
    if (organizationActive === true) {
        cards.push(readyCard('organization_active', activeTitle));
    } else if (organizationActive === false) {
        cards.push(
            pendingCard(
                'organization_active',
                activeTitle,
                IDENTITY_PENDING_WARNINGS['organization_active'],
                'blocked',
                'Inactive',
            ),
        );
    } else {
        cards.push({
            key: 'organization_active',
            title: activeTitle,
            status: 'unknown',
            statusLabel: 'Unknown',
            detail: 'Organization status is not available yet.',
        });
    }

    const order = [
        'organization_created',
        'organization_active',
        'admin_staff_created',
        'admin_mobile_verified',
    ];
    return order
        .map((key) => cards.find((card) => card.key === key))
        .filter((card): card is OperationalStatusCardView => !!card);
}

export function mapInstallationCards(
    items: OnboardingChecklistItem[],
): OperationalStatusCardView[] {
    return INSTALLATION_CHECKLIST_KEYS.map((key) => {
        const item = checklistItemByKey(items, key);
        const title = INSTALLATION_TITLES[key];
        if (item?.completed) {
            return readyCard(key, title);
        }
        return pendingCard(key, title, INSTALLATION_PENDING_WARNINGS[key]);
    });
}
